import { useRef, useState, useEffect, useCallback, useMemo } from "react"
import type { GuidebookData, GuidebookPlace } from "./types"
import "./guidebook.css"
import { unlockGuestGuidebook } from "./api"
import { getVoiceCopy } from "../house-voice-copy"
import { CoverHero } from "./CoverHero"
import { StickyTopBar } from "./StickyTopBar"
import { BottomSheetTOC } from "./BottomSheetTOC"
import { BackToTop } from "./BackToTop"
import { WelcomeCard } from "./WelcomeCard"
import { AccessEssentials } from "./AccessEssentials"
import { PropertyInfoSection } from "./PropertyInfoSection"
import { ArrivalDepartureSection } from "./ArrivalDepartureSection"
import { HouseRulesSection } from "./HouseRulesSection"
import { PlacesSection } from "./PlacesSection"
import { EmergencySection } from "./EmergencySection"
import { HighlightsStrip } from "./HighlightsStrip"

interface Props {
  data: GuidebookData
  slug?: string
  guestToken?: string
  onRefresh?: () => void
  showUnlockPrompt?: boolean
  onOpenUnlock?: () => void
}

// New three-zone section order: Access → Welcome → Highlights → Info → Stay → Rules → Places → Emergency
const SECTION_IDS = ["access", "welcome", "highlights", "info", "stay", "rules", "places", "emergency"]

export function GuidebookShell({
  data,
  slug,
  guestToken,
  onRefresh,
  showUnlockPrompt,
  onOpenUnlock,
}: Props) {
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})
  const heroRef = useRef<HTMLDivElement>(null)
  const tocDialogRef = useRef<HTMLDialogElement>(null)
  const [activeSection, setActiveSection] = useState("")
  const [scrolled, setScrolled] = useState(false)
  const [pastHero, setPastHero] = useState(false)
  const [tocOpen, setTocOpen] = useState(false)
  const [pin, setPin] = useState("")
  const [unlocking, setUnlocking] = useState(false)
  const [unlockError, setUnlockError] = useState<string | null>(null)
  const [unlockSuccess, setUnlockSuccess] = useState<string | null>(null)
  const [pendingHighlightPlace, setPendingHighlightPlace] = useState<GuidebookPlace | null>(null)

  const propertyInitial = data.property.name?.charAt(0)?.toUpperCase() || "?"
  const voiceCopy = useMemo(() => getVoiceCopy(data.house_voice), [data.house_voice])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    if (!heroRef.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setPastHero(!entry.isIntersecting)
      },
      { threshold: 0 },
    )
    observer.observe(heroRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    for (const id of SECTION_IDS) {
      const el = sectionRefs.current[id]
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [data])

  const scrollTo = useCallback((id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [])

  // Hero CTA scrolls to access essentials (urgency-first)
  const onExplore = useCallback(() => {
    scrollTo("access")
  }, [scrollTo])

  const handleHighlightSelect = useCallback((place: GuidebookPlace) => {
    setPendingHighlightPlace(place)
    scrollTo("places")
  }, [scrollTo])

  const scrollToEmergency = useCallback(() => {
    scrollTo("emergency")
  }, [scrollTo])

  const openTOC = useCallback(() => {
    tocDialogRef.current?.showModal()
    setTocOpen(true)
  }, [])

  const closeTOC = useCallback(() => {
    tocDialogRef.current?.close()
    setTocOpen(false)
  }, [])

  const setRef = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      sectionRefs.current[id] = el
    },
    [],
  )

  const hasEnabledRules = Object.values(data.houseRules || {}).some(
    (group) => typeof group === "object" && group !== null && "enabled" in group && (group as { enabled: boolean }).enabled,
  )

  const access = data.access
  const canUnlockPin = Boolean(slug && guestToken && onRefresh)
  const showPinUnlock = Boolean(canUnlockPin && access?.sensitive_locked)
  const pinRequired = Boolean(access?.pin_required)

  const sensitiveFields = Array.isArray(data.sensitiveFields) ? data.sensitiveFields : []
  const wifiLocked = sensitiveFields.includes("wifi_password")
    || Boolean(access?.sensitive_locked && data.property.wifi_name && !data.property.wifi_password)
  const doorLocked = sensitiveFields.includes("door_code")
    || Boolean(access?.sensitive_locked && !data.property.door_code && data.property.door_code_status === "visible")

  const hasAccessSection = Boolean(
    data.property.wifi_name || wifiLocked || doorLocked
    || data.property.door_code || data.property.door_code_status === "pending"
    || data.property.checkin_time || data.houseRules?.checkout?.checkoutTime
  )
  const hasStaySection = Boolean(
    data.strategy
    || (Array.isArray(data.houseRules?.checkout?.checklist) && data.houseRules.checkout.checklist.length > 0),
  )

  const doorCodeVisibility = data.doorCodeVisibility
    || (data.property.door_code_status === "pending"
      ? "available_on_arrival"
      : data.property.door_code_status === "visible"
        ? "available_now"
        : "locked")

  const checkoutTime = data.houseRules?.checkout?.checkoutTime

  return (
    <>
      <div ref={heroRef}>
        <CoverHero
          property={data.property}
          welcome={data.welcome}
          weather={data.weather}
          onExplore={onExplore}
          scrolled={scrolled}
        />
      </div>

      <StickyTopBar
        propertyName={data.property.name}
        propertyInitial={propertyInitial}
        visible={pastHero}
        onOpenTOC={openTOC}
        tocOpen={tocOpen}
        onScrollToEmergency={scrollToEmergency}
      />

      <BottomSheetTOC
        data={data}
        activeSection={activeSection}
        onNavigate={scrollTo}
        dialogRef={tocDialogRef}
        onClose={closeTOC}
      />

      <div className="guidebook__content">
        {/* ── Zone 1: Arrival ── */}

        {/* Access Essentials — unified credential card (door + Wi-Fi + times) */}
        {hasAccessSection && (
          <div ref={setRef("access")} id="access">
            <AccessEssentials
              property={data.property}
              checkinTime={data.property.checkin_time}
              checkoutTime={checkoutTime}
              doorLocked={doorLocked}
              wifiLocked={wifiLocked}
              doorCodeVisibility={doorCodeVisibility}
              doorCodePolicyText={data.doorCodePolicyText}
              onUnlock={showUnlockPrompt ? onOpenUnlock : undefined}
            />
          </div>
        )}

        {/* Unlock prompts (PIN only — booking verification is handled inline in AccessEssentials) */}
        {showPinUnlock && (
          <div className="gb-section">
            <div className="gb-card gb-card--locked">
              <div className="gb-lock__title">Unlock private entry details</div>
              <div className="gb-lock__body">
                {pinRequired
                  ? "For security, this property protects entry details with a short access PIN."
                  : "Private entry details are locked for this stay. Ask your host for access."}
              </div>

              {pinRequired && (
                <div style={{ display: "grid", gap: "0.65rem" }}>
                  <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={pin}
                    onChange={(event) => setPin(event.target.value)}
                    placeholder="Access PIN"
                    style={{
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 12,
                      padding: "0.68rem 0.78rem",
                      fontSize: "1rem",
                      background: "rgba(255, 255, 255, 0.9)",
                    }}
                  />
                  {unlockError && (
                    <div style={{ color: "#b42318", fontSize: "0.88rem", lineHeight: 1.35 }}>
                      {unlockError}
                    </div>
                  )}
                  {unlockSuccess && (
                    <div style={{ color: "var(--gb-accent)", fontSize: "0.88rem", lineHeight: 1.35 }}>
                      {unlockSuccess}
                    </div>
                  )}
                  <button
                    className="gb-copy-btn"
                    type="button"
                    disabled={unlocking}
                    onClick={async () => {
                      if (unlocking) return
                      if (!pin.trim()) {
                        setUnlockError("Enter the access PIN from your host.")
                        return
                      }
                      setUnlocking(true)
                      setUnlockError(null)
                      setUnlockSuccess(null)
                      try {
                        await unlockGuestGuidebook(String(slug), String(guestToken), pin)
                        setUnlockSuccess("Unlocked.")
                        setPin("")
                        onRefresh?.()
                      } catch (err) {
                        setUnlockError(err instanceof Error ? err.message : "Incorrect PIN.")
                      } finally {
                        setUnlocking(false)
                      }
                    }}
                    style={{ width: "100%" }}
                  >
                    {unlocking ? "Unlocking..." : "Unlock details"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Welcome — editorial text, no card wrapper — gb-welcome provides its own spacing */}
        <div ref={setRef("welcome")} id="welcome">
          <WelcomeCard property={data.property} welcome={data.welcome} greetingAfternoon={voiceCopy.gb_welcome_greeting_afternoon} />
        </div>

        {/* ── Zone 2: Today ── */}

        {/* Highlights + Weather context merged */}
        {data.highlights && data.highlights.length > 0 && (
          <div ref={setRef("highlights")} id="highlights">
            <HighlightsStrip
              highlights={data.highlights}
              places={data.places}
              weather={data.weather}
              onSelectPlace={handleHighlightSelect}
            />
          </div>
        )}

        {/* ── Zone 3: Reference ── */}

        {/* About the area */}
        <div ref={setRef("info")} id="info">
          <PropertyInfoSection property={data.property} />
        </div>

        {/* Stay summary + departure checklist */}
        {hasStaySection && (
          <div ref={setRef("stay")} id="stay">
            <ArrivalDepartureSection
              strategy={data.strategy}
              houseRules={data.houseRules}
              checkinTime={data.property.checkin_time}
            />
          </div>
        )}

        {/* Good to know (house rules) */}
        {hasEnabledRules && (
          <div ref={setRef("rules")} id="rules">
            <HouseRulesSection rules={data.houseRules} sectionTitle={voiceCopy.gb_section_rules} />
          </div>
        )}

        {/* Places we love */}
        {data.places.length > 0 && (
          <div ref={setRef("places")} id="places">
            <PlacesSection
              places={data.places}
              externalOpenPlace={pendingHighlightPlace}
              onExternalOpenHandled={() => setPendingHighlightPlace(null)}
              sectionTitle={voiceCopy.gb_section_places}
            />
          </div>
        )}

        {/* Need help? */}
        <div ref={setRef("emergency")} id="emergency">
          <EmergencySection property={data.property} sectionTitle={voiceCopy.gb_section_emergency} bodyText={voiceCopy.gb_emergency_body} />
        </div>

        <footer className="guidebook__footer">
          Made with <a href="https://stayframe.com" target="_blank" rel="noopener noreferrer">Stayframe</a>
        </footer>
      </div>

      <BackToTop visible={pastHero} />
    </>
  )
}
